import { useState, useEffect } from 'react';
import { X, ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from './ui/button';
import { Dialog, DialogContent } from './ui/dialog';
import { storyApi } from '../services/api';
import { Spinner } from './ui/spinner';
import { getImageUrl } from '../utils/imageUrl';

interface Slide {
  _id: string;
  image_url?: string;
  narration?: string;
  position: number;
}

interface SlidesViewModalProps {
  isOpen: boolean;
  onClose: () => void;
  storyId: string;
  storyTitle: string;
}

export function SlidesViewModal({ isOpen, onClose, storyId, storyTitle }: SlidesViewModalProps) {
  const [slides, setSlides] = useState<Slide[]>([]);
  const [currentSlideIndex, setCurrentSlideIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [imageLoading, setImageLoading] = useState(false);

  useEffect(() => {
    if (isOpen && storyId) {
      fetchSlides();
    }
  }, [isOpen, storyId]);

  const fetchSlides = async () => {
    setLoading(true);
    try {
      const response = await storyApi.getStory(storyId);
      const slidesData = response.data.data?.slides || [];
      setSlides(slidesData.sort((a: Slide, b: Slide) => a.position - b.position));
      setCurrentSlideIndex(0);
    } catch (error) {
      console.error('Failed to fetch slides:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // Set image loading when slide changes
    const slide = slides[currentSlideIndex];
    if (slide?.image_url) {
      setImageLoading(true);
    }
  }, [currentSlideIndex, slides]);

  const goToPrevious = () => {
    setCurrentSlideIndex((prev) => Math.max(0, prev - 1));
  };

  const goToNext = () => {
    setCurrentSlideIndex((prev) => Math.min(slides.length - 1, prev + 1));
  };

  const handleImageLoad = () => {
    setImageLoading(false);
  };

  const handleImageError = () => {
    setImageLoading(false);
  };

  const currentSlide = slides[currentSlideIndex];

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-[95vw] w-full max-h-[95vh] h-[95vh] p-0 overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b flex-shrink-0">
          <div>
            <h2 className="text-lg font-semibold">{storyTitle}</h2>
            <p className="text-sm text-muted-foreground">
              Slide {currentSlideIndex + 1} of {slides.length}
            </p>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={onClose}
            className="rounded-full"
          >
            <X className="h-4 w-4" />
          </Button>
        </div>

        {/* Content */}
        <div className="flex-1 min-h-0 overflow-hidden">
          {loading ? (
            <div className="flex items-center justify-center h-full">
              <Spinner size="lg" />
            </div>
          ) : slides.length === 0 ? (
            <div className="flex items-center justify-center h-full">
              <p className="text-muted-foreground">No slides available</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-2 h-full gap-4 p-4">
              {/* Image Section */}
              <div className="flex items-center justify-center">
                <div className="w-full max-w-lg mx-auto relative">
                  {currentSlide?.image_url ? (
                    <>
                      {imageLoading && (
                        <div className="absolute inset-0 flex items-center justify-center bg-muted rounded-lg">
                          <Spinner size="md" />
                        </div>
                      )}
                      <img
                        key={currentSlideIndex}
                        src={getImageUrl(currentSlide.image_url)}
                        alt={`Slide ${currentSlideIndex + 1}`}
                        className={`w-full h-auto object-contain rounded-lg shadow-lg transition-opacity duration-200 ${
                          imageLoading ? 'opacity-0' : 'opacity-100'
                        }`}
                        style={{ maxHeight: '60vh' }}
                        onLoad={handleImageLoad}
                        onError={handleImageError}
                      />
                    </>
                  ) : (
                    <div className="flex items-center justify-center w-full aspect-video bg-muted rounded-lg">
                      <p className="text-muted-foreground">No image available</p>
                    </div>
                  )}
                </div>
              </div>

              {/* Narration Section */}
              <div className="flex items-center justify-center">
                <div className="max-w-prose w-full p-4">
                  <p className="text-base lg:text-lg leading-relaxed">
                    {currentSlide?.narration || 'No narration available'}
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Navigation */}
        {slides.length > 0 && (
          <div className="flex items-center justify-between p-4 border-t flex-shrink-0">
            <Button
              variant="outline"
              onClick={goToPrevious}
              disabled={currentSlideIndex === 0}
            >
              <ChevronLeft className="h-4 w-4 mr-2" />
              Previous
            </Button>

            <div className="flex gap-1">
              {slides.map((_, index) => (
                <button
                  key={index}
                  onClick={() => setCurrentSlideIndex(index)}
                  className={`w-2 h-2 rounded-full transition-colors ${
                    index === currentSlideIndex ? 'bg-primary' : 'bg-muted-foreground/30'
                  }`}
                  aria-label={`Go to slide ${index + 1}`}
                />
              ))}
            </div>

            <Button
              variant="outline"
              onClick={goToNext}
              disabled={currentSlideIndex === slides.length - 1}
            >
              Next
              <ChevronRight className="h-4 w-4 ml-2" />
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}